import store from '@/store/store'
import { setShowModalType } from '@/store/slices/modal/action'
import { ShowModalType } from '@/scenes/engine/common/constants'
import { SignInManager, getSpecialOfferSuperMonthlyPriceId } from '@/scenes/Editor/components/Navbar/components/SignInManager'
import { shouldShowLimitedTimeOffer, type LimitedTimeOfferGate } from './limitedTimeOfferGate'
import {
  canShowLimitedTimeOffer, readLimitedTimeOfferHistory, writeLimitedTimeOfferHistory,
  recordLimitedTimeOfferImpression, recordLimitedTimeOfferDismissal, recordLimitedTimeOfferAcceptance,
  type LimitedTimeOfferTrigger,
} from './limitedTimeOfferHistory'

export interface LimitedTimeOfferUpgradeOwner {
  readonly subscriptionId: string
}

interface PendingOffer extends LimitedTimeOfferUpgradeOwner {
  userId: string
  subscriptionId: string
  trigger: LimitedTimeOfferTrigger
  shown: boolean
  upgrading: boolean
  onDismiss?: () => void
  resolve?: (resume: boolean) => void
  unsubscribe: () => void
}
let pendingOffer: PendingOffer | null = null
const admittedUpgrades = new WeakSet<PendingOffer>()
let postPurchaseRequest: { userId: string; path: string; cancel: () => void; unsubscribe: () => void } | null = null

function cancelPostPurchaseRequest(): void {
  const request = postPurchaseRequest
  if (!request) return
  postPurchaseRequest = null
  request.unsubscribe()
  request.cancel()
}

export function preserveLimitedTimeOfferForProjectUrl(before: string, next: string): void {
  if (before === '/' && /^\/design\/[^/]+\/edit/.test(next) && postPurchaseRequest?.path === before) postPurchaseRequest.path = next
}

function currentUserId(): string | null {
  const user = store.getState().editor.user.user
  const id = user?.userId ?? user?.id
  return id == null ? null : String(id)
}

function finishOffer(resume: boolean, beforeResume?: () => void): void {
  const pending = pendingOffer
  if (!pending) return
  pendingOffer = null
  pending.unsubscribe()
  beforeResume?.()
  pending.resolve?.(resume && currentUserId() === pending.userId)
  if (resume && currentUserId() === pending.userId) pending.onDismiss?.()
}

export function maybeShowLimitedTimeOfferForTrigger(
  trigger: LimitedTimeOfferTrigger,
  onDismiss?: () => void,
): boolean {
  const state = store.getState()
  const userId = currentUserId()
  if (pendingOffer || !userId || !state.editor.user.userIsPremium || state.editor.user.userIsSuper ||
    state.modal.showModalType !== null || state.editor.user.openModalTryPremium.isOpen ||
    state.editor.user.openModalOutOfCredits.isOpen || !getSpecialOfferSuperMonthlyPriceId()) return false
  const subscription = SignInManager.getInstance().getLimitedTimeOfferSubscription()
  if (!subscription) return false
  const history = readLimitedTimeOfferHistory(userId)
  if (!history || !canShowLimitedTimeOffer(history, trigger, Date.now())) return false
  const pending: PendingOffer = {
    userId, subscriptionId: subscription.subscriptionId, trigger, shown: false, upgrading: false,
    onDismiss, unsubscribe: () => {},
  }
  pendingOffer = pending
  pending.unsubscribe = store.subscribe(() => {
    if (pendingOffer !== pending) return
    if (currentUserId() !== pending.userId || store.getState().modal.showModalType !== ShowModalType.LIMITED_TIME_OFFER) {
      finishOffer(false)
    }
  })
  store.dispatch(setShowModalType(ShowModalType.LIMITED_TIME_OFFER))
  return true
}

/** Existing post-purchase tutorial hand-off; all campaign counts are shared. */
export function maybeShowLimitedTimeOffer(gate: LimitedTimeOfferGate & { onDismiss?: () => void }): boolean {
  return shouldShowLimitedTimeOffer(gate) && maybeShowLimitedTimeOfferForTrigger('post-purchase', gate.onDismiss)
}

/** Only the post-checkout hand-off waits briefly for webhook/Store ingestion. */
export async function completePostPurchaseLimitedTimeOffer(gate: LimitedTimeOfferGate & { onDismiss: () => void }): Promise<void> {
  if (postPurchaseRequest) return
  if (!shouldShowLimitedTimeOffer(gate)) { gate.onDismiss(); return }
  const userId = currentUserId()
  if (!userId) return
  if (maybeShowLimitedTimeOffer(gate)) return
  const state = store.getState()
  if (pendingOffer || state.modal.showModalType !== null || state.editor.user.openModalTryPremium.isOpen || state.editor.user.openModalOutOfCredits.isOpen) return
  let cancel: () => void = () => {}
  const cancelled = new Promise<void>(resolve => { cancel = resolve })
  const request = { userId, path: window.location.pathname, cancel, unsubscribe: () => {} }
  postPurchaseRequest = request
  request.unsubscribe = store.subscribe(() => {
    const current = store.getState()
    if (currentUserId() !== userId || current.modal.showModalType !== null || current.editor.user.openModalTryPremium.isOpen || current.editor.user.openModalOutOfCredits.isOpen) cancelPostPurchaseRequest()
  })
  let timeout: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<void>(resolve => { timeout = setTimeout(resolve, 3000) })
  try {
    await Promise.race([SignInManager.getInstance().refreshTransactionStatus(10), deadline, cancelled])
  } catch {
    // Optional offer failure continues the successful purchase flow.
  } finally {
    if (timeout !== undefined) clearTimeout(timeout)
    request.unsubscribe()
  }
  if (postPurchaseRequest !== request) return
  postPurchaseRequest = null
  if (currentUserId() !== userId || window.location.pathname !== request.path) return
  if (!maybeShowLimitedTimeOffer(gate)) gate.onDismiss()
}

/** No API wait: unknown eligibility refreshes in the background and lets the action proceed. */
export function waitForLimitedTimeOffer(): Promise<boolean> {
  if (pendingOffer) return Promise.resolve(false)
  if (!maybeShowLimitedTimeOfferForTrigger('download')) return Promise.resolve(true)
  const userId = currentUserId()
  return new Promise<boolean>(resolve => { if (pendingOffer) pendingOffer.resolve = resolve; else resolve(false) })
    .then(resume => resume && currentUserId() === userId)
}

/** Called from the visible modal, not from the attempt to open it. */
export function markLimitedTimeOfferVisible(): boolean {
  const pending = pendingOffer
  if (!pending || pending.shown || currentUserId() !== pending.userId) return false
  const history = readLimitedTimeOfferHistory(pending.userId)
  if (!history || !canShowLimitedTimeOffer(history, pending.trigger, Date.now()) ||
    !writeLimitedTimeOfferHistory(pending.userId, recordLimitedTimeOfferImpression(history, Date.now()))) {
    finishOffer(true, () => store.dispatch(setShowModalType(null)))
    return false
  }
  pending.shown = true
  return true
}

export function beginLimitedTimeOfferUpgrade(): LimitedTimeOfferUpgradeOwner | null {
  const pending = pendingOffer
  const user = store.getState().editor.user
  if (!pending || pending.upgrading || currentUserId() !== pending.userId || !user.userIsPremium || user.userIsSuper) return null
  // Admission captured the subscription; billing revalidates that exact id after
  // refreshing. A cache miss must not prevent starting that refresh.
  pending.upgrading = true
  admittedUpgrades.add(pending)
  return pending
}

export function settleLimitedTimeOfferUpgrade(owner: object, succeeded: boolean): boolean {
  const operation = owner as PendingOffer
  if (succeeded) recordLimitedTimeOfferUpgradeApplied(owner)
  if (!admittedUpgrades.delete(operation)) return false
  if (pendingOffer !== operation || currentUserId() !== operation.userId) return false
  operation.upgrading = false
  return true
}

/** A successful plan mutation survives cancellation of its presentation. */
export function recordLimitedTimeOfferUpgradeApplied(owner: object): void {
  const operation = owner as PendingOffer
  if (admittedUpgrades.has(operation) && operation.shown) {
    const history = readLimitedTimeOfferHistory(operation.userId)
    if (history && history.acceptedAt === null) writeLimitedTimeOfferHistory(operation.userId, recordLimitedTimeOfferAcceptance(history, Date.now()))
  }
}

export function closeLimitedTimeOffer(dismissed: boolean): boolean {
  const pending = pendingOffer
  if (pending?.upgrading) return false
  const actualDismissal = Boolean(dismissed && pending?.shown && currentUserId() === pending.userId)
  if (dismissed && pending?.shown && currentUserId() === pending.userId) {
    const history = readLimitedTimeOfferHistory(pending.userId)
    if (history) writeLimitedTimeOfferHistory(pending.userId, recordLimitedTimeOfferDismissal(history, Date.now()))
  }
  // Release the owning slot before dispatching or running a continuation.
  if (pending) finishOffer(true, () => store.dispatch(setShowModalType(null)))
  else store.dispatch(setShowModalType(null))
  return actualDismissal
}

export function cancelLimitedTimeOffer(): void {
  cancelPostPurchaseRequest()
  if (!pendingOffer) return
  finishOffer(false)
  if (store.getState().modal.showModalType === ShowModalType.LIMITED_TIME_OFFER) store.dispatch(setShowModalType(null))
}

/** Debug presentation does not alter campaign counts or resume an action. */
export function openLimitedTimeOfferForDebug(): void {
  cancelLimitedTimeOffer()
  store.dispatch(setShowModalType(ShowModalType.LIMITED_TIME_OFFER))
}

/** A successful export's existing result dialog owns the screen until it closes. */
export function offerAfterCurrentResultDialog(): void {
  const userId = currentUserId()
  const path = window.location.pathname
  const generation = store.getState().modal.customTextProps?.callbackGeneration
  if (!userId || generation == null) return
  const unsubscribe = store.subscribe(() => {
    const state = store.getState().modal
    if (currentUserId() !== userId || window.location.pathname !== path ||
      (state.showModalType !== null && (state.showModalType !== ShowModalType.CUSTOM_TEXT || state.customTextProps?.callbackGeneration !== generation))) {
      unsubscribe()
      return
    }
    if (state.showModalType === null) {
      unsubscribe()
      maybeShowLimitedTimeOfferForTrigger('export')
    }
  })
}
